import { useState } from "react";
import { Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CenyAMarzeDialog } from "@/components/zakazka/CenyAMarzeDialog";
import { CollapsibleSection } from "@/components/zakazka/CollapsibleSection";
import { formatEur, formatPct } from "@/lib/format";
import type { Varianta, ZakazkaDetail } from "@/api/schemas";

export interface ZakazkaSummaryProps {
  zakazka: ZakazkaDetail;
  varianta: Varianta;
}

export function ZakazkaSummary({
  zakazka,
  varianta,
}: ZakazkaSummaryProps): JSX.Element {
  const [cielOpen, setCielOpen] = useState(false);
  const vyrobne = (zakazka.varianty ?? []).filter((v) => v.je_vyrobna);
  // Výroba spolu (KOLO 208) — na výpočte, keď zákazka výrobu má (porovnanie
  // ceny pre zákazníka s výrobnou kalkuláciou), na výrobe pri viacerých častiach
  const ukazVyrobu = varianta.je_vyrobna ? vyrobne.length > 1 : vyrobne.length > 0;

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      {/* Súčet zostáva VŽDY na očiach — je to číslo, s ktorým sa pracuje.
          Do rolety ide len rozpis (bez DPH, DPH, sleva, doprava, montáž). */}
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {varianta.je_cast && vyrobne.length > 1 ? "Časť s DPH" : "S DPH"}
        </h2>
        <div className="flex items-baseline gap-1">
          <span className="font-mono text-base font-semibold text-primary">
            {formatEur(varianta.cena_s_dph)}
          </span>
          {/* Pult sedí pri cene, ktorú mení — nie v ⋮ menu */}
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 shrink-0"
            aria-label="Ceny a marže"
            title="Ceny a marže — zisk na celý výpočet, marže zostáv, sleva a cieľová cena"
            onClick={() => setCielOpen(true)}
          >
            <Target className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {ukazVyrobu ? (
        <div className="flex items-baseline justify-between text-sm" data-testid="vyroba-spolu">
          <span className="text-muted-foreground">
            Výroba spolu
            {vyrobne.length > 1
              ? ` (${vyrobne.length} ${vyrobne.length < 5 ? "časti" : "častí"})`
              : ""}
          </span>
          <span className="font-mono font-semibold">
            {formatEur(zakazka.cena_vyroby_s_dph)}
          </span>
        </div>
      ) : null}

      <CollapsibleSection title="Rozpis ceny" storageKey="cena_rozpis">
        <div className="space-y-1.5 pt-1 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Bez DPH</span>
            <span className="font-mono">{formatEur(varianta.cena_bez_dph)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              DPH {formatPct(varianta.dph_pct)}
            </span>
            <span className="font-mono">{formatEur(varianta.dph)}</span>
          </div>
          {Number(varianta.sleva_pct) > 0 ? (
            <div className="flex justify-between text-muted-foreground">
              <span>Zľava {formatPct(varianta.sleva_pct)}</span>
              <span className="font-mono">—</span>
            </div>
          ) : null}
          <Separator />
          <div className="space-y-1 pt-1 text-xs text-muted-foreground">
            <div>Doprava: {zakazka.doprava ? "v cene" : "osobný odber"}</div>
            <div>Montáž: {zakazka.montaz ? "v cene" : "neobsahuje"}</div>
          </div>
        </div>
      </CollapsibleSection>

      {varianta.je_vyrobna ? (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
          Výroba — výrobná kalkulácia (čo sa reálne vyrába), nie cena pre zákazníka.
        </p>
      ) : null}

      {/* Až po kliknutí — zavretý dialóg by inak držal mutačné hooky aj
          dotaz na náhľad ceny v každom paneli s cenou. */}
      {cielOpen ? (
        <CenyAMarzeDialog
          open
          onOpenChange={setCielOpen}
          zakazkaId={zakazka.id}
          varianta={varianta}
        />
      ) : null}
    </div>
  );
}
